<template>
    <DialogLayout :class-name="'resume-result-dialog'" @close="handleClose">
        <template #title>{{ props.title }}</template>
        <template #default>
            <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'left'" :label-width="'100px'"
                :validate-on-rule-change="false" class="resume-result-form">
                <el-form-item :label="t('candidate.result')" prop="result">
                    <el-radio-group v-model="formData.result" :disabled="isResultDisabled">
                        <el-radio :value="1">合格</el-radio>
                        <el-radio :value="2">不合格</el-radio>
                    </el-radio-group>
                </el-form-item>

                <el-form-item :label="t('candidate.comment')" prop="comment" label-position="top" required>
                    <el-input v-model="formData.comment" type="textarea" :placeholder="t('candidate.commentPlaceholder')" :rows="4" maxlength="500"
                        show-word-limit />
                </el-form-item>

                <el-form-item v-if="showOfferLetterUpload" prop="offerLetter" label-width="0" class="offer-letter-item">
                    <FileUpload
                        v-loading="uploadingOfferLetter"
                        class="resume-result-file"
                        :title="t('candidate.offerLetter')"
                        :tips="t('candidate.offerLetterTips')"
                        accept=".pdf"
                        :max-count="5"
                        :disabled="uploadingOfferLetter"
                        :file-list="offerLetterFiles"
                        @update:file-list="handleOfferLetterListUpdate"
                        @upload="handleOfferLetterUpload"
                    />
                </el-form-item>
            </el-form>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" :loading="submitting" @click="handleConfirm">
                {{ confirmButtonText }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import FileUpload from '@/components/business-components/file-upload/index.vue'
import type { UploadedFile } from '@/components/business-components/file-upload/types'
import type { ResumeResultData, ResumeResultDialogProps } from './resume-result-dialog-types'
import { submitInterviewEvaluationApi, uploadAttachmentApi } from '@/api/candidate'
import { getIsLastInterviewApi } from '@/api/interview'
import { useI18n } from 'vue-i18n'

// 内定通知书上传文件（在 FileUpload 通用类型基础上补充下载地址）
interface OfferLetterUploadItem extends UploadedFile {
    downloadUrl?: string
}

const { t } = useI18n()
const props = defineProps<ResumeResultDialogProps>()

const emit = defineEmits<{
    (e: 'confirm', data: ResumeResultData): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const submitting = ref(false)

// 按钮文本
const confirmButtonText = computed(() => {
    return props.data ? t('common.save') : t('common.confirm1')
})

// 表单数据
const formData = reactive<ResumeResultData>({
    result: undefined,
    comment: ''
})

// 是否是最后一轮面试（次ステップが内定）
const isLastInterview = ref(false)
// 内定通知书文件列表
const offerLetterFiles = ref<OfferLetterUploadItem[]>([])
// 内定通知书上传中状态
const uploadingOfferLetter = ref(false)

// 如果有回显数据，结果不可更改
const isResultDisabled = computed(() => {
    return props.data?.result !== undefined && props.data?.result !== null
})

// 最后一轮面试且合格时，展示并要求上传内定通知书
const showOfferLetterUpload = computed(() => {
    return false; // 20260807 先去掉内定通知书的上传
    return isLastInterview.value && formData.result === 1
})

// 表单验证规则
const rules = computed<FormRules>(() => {
    const baseRules: FormRules = {
        comment: [
            { required: true, message: t('candidate.pleaseEnterComment'), trigger: 'blur' }
        ]
    }

    if (showOfferLetterUpload.value) {
        baseRules.offerLetter = [
            {
                required: true,
                validator: (_rule, _value, callback) => {
                    if (!offerLetterFiles.value.length) {
                        callback(new Error(t('candidate.pleaseUploadOfferLetter')))
                    } else {
                        callback()
                    }
                },
                trigger: 'change'
            }
        ]
    }

    return baseRules
})

// 处理内定通知书文件列表更新
const handleOfferLetterListUpdate = (fileList: OfferLetterUploadItem[]) => {
    offerLetterFiles.value = fileList
    formRef.value?.clearValidate('offerLetter')
}

// 上传内定通知书
const handleOfferLetterUpload = async (file: File) => {
    uploadingOfferLetter.value = true
    try {
        const result = await uploadAttachmentApi(file)
        const raw = result as unknown as { name?: string; fileName?: string; s3key?: string; s3Key?: string; url?: string; previewUrl?: string; downloadUrl?: string }
        const newFile: OfferLetterUploadItem = {
            uid: raw.s3key ?? raw.s3Key,
            fileName: raw.fileName ?? raw.name,
            url: raw.previewUrl ?? raw.url,
            previewUrl: raw.previewUrl ?? raw.url,
            downloadUrl: raw.downloadUrl ?? raw.url,
            fileKey: raw.s3key ?? raw.s3Key
        }
        offerLetterFiles.value = [...offerLetterFiles.value, newFile]
        formRef.value?.clearValidate('offerLetter')
        ElMessage.success(t('common.uploadSuccess'))
    } catch (error) {
        console.error('内定通知書のアップロードに失敗しました:', error)
        ElMessage.error((error as Error)?.message || t('common.uploadFailed'))
    } finally {
        uploadingOfferLetter.value = false
    }
}

// 初始化表单数据
onMounted(async () => {
    if (props.data) {
        formData.result = props.data.result
        formData.comment = props.data.comment || ''
        offerLetterFiles.value = (props.data.offerLetter || []).map((item) => ({
            fileName: item.fileName,
            url: item.previewUrl || item.downloadUrl,
            previewUrl: item.previewUrl,
            downloadUrl: item.downloadUrl,
            fileKey: item.s3Key
        }))
    } else {
        formData.result = 1
        formData.comment = ''
    }

    if (props.processId) {
        try {
            isLastInterview.value = await getIsLastInterviewApi(props.processId)
        } catch (error) {
            console.error('判断是否是最后一轮面试失败:', error)
            isLastInterview.value = false
        }
    }
})

// 确认
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        // 先验证表单，如果验证失败会抛出错误，不会继续执行
        await formRef.value.validate()
    } catch (error: unknown) {
        // 表单验证失败，不显示额外提示（Element Plus 会自动显示），直接返回，不提交接口
        if (error && typeof error === 'object' && 'fields' in error) {
            return
        }
        // 其他验证错误也直接返回
        return
    }

    // 验证通过后，才执行 API 调用
    try {
        submitting.value = true

        // 调用 API 提交/更新评估结果
        // status 由使用处按需传入（传 4 触发状态机，不传默认是4会触发状态流转）
        const offerLetterS3Key = showOfferLetterUpload.value
            ? offerLetterFiles.value.map((file) => file.fileKey).filter((key): key is string => !!key)
            : undefined
        await submitInterviewEvaluationApi({
            processId: Number(props.processId),
            type: props.type,
            result: formData.result!,
            comments: formData.comment || '',
            ...(props.status !== undefined ? { status: props.status } : {}),
            ...(offerLetterS3Key ? { offerLetterS3Key } : {})
        })

        // API 调用成功，将结果传递给父组件并关闭对话框
        const resultData: ResumeResultData = {
            result: formData.result,
            comment: formData.comment,
            ...(showOfferLetterUpload.value ? {
                offerLetter: offerLetterFiles.value.map((file) => ({
                    fileName: file.fileName,
                    previewUrl: file.previewUrl,
                    downloadUrl: file.downloadUrl
                }))
            } : {})
        }
        
        ElMessage.success(t('common.saveSuccess'))
        submitting.value = false
        // emit('confirm') 会自动关闭对话框，Promise 会 resolve
        emit('confirm', resultData)
    } catch (error: unknown) {
        submitting.value = false
        
        // API 调用失败
        const errorMessage = error && typeof error === 'object' && 'message' in error 
            ? String(error.message) 
            : t('common.saveFailed')
        console.error('提交失败:', error)
        ElMessage.error(errorMessage)
    }
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.resume-result-form {

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 35px;
        text-align: right;
        padding-right: 12px;
        margin-bottom: 0px;

        &::before {
            display: none;
        }
    }

    :deep(.el-form-item--label-top .el-form-item__label) {
        text-align: left;
        padding-right: 0;
        line-height: normal;
        margin-bottom: 8px;
    }

    :deep(.el-radio-group) {
        display: flex;
        gap: 20px;
    }

    :deep(.el-radio) {
        margin-right: 0;

        .el-radio__label {
            font-size: 14px;
            color: #495057;
            padding-left: 8px;
        }
    }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }

    .offer-letter-item {
        :deep(.el-form-item__content) {
            display: block;
        }

        :deep(.upload-title)::after {
            content: '*';
            color: #F53F3F;
            margin-left: 4px;
        }
    }
}

:deep(.resume-result-dialog) {
    width: 512px;

    

}

:deep(.resume-result-file) {
    // background: red;
    .content {
        background: #F7F8FA;
        border-radius: 8px;
        padding: 8px;
    }
}
</style>

<style lang="scss">
.resume-result-dialog {
    width: 512px !important;
    .dialog-footer {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
    
    
            .el-button {
                min-width: 80px;
            }
        }
}
 //.dialog-footer {
    //padding: 0px 30px 20px 30px!important;
 //}
</style>

